import { Link } from 'react-router-dom';
import { Users, Truck, DollarSign, CheckCircle, Settings, ArrowRight } from 'lucide-react';
import { useFetch } from '../lib/useFetch';
import { rideStatusLabels, rideStatusColors, rideTypeLabels, formatFcfa, formatDate } from '../lib/labels';
import { PaginatedResponse } from '@ouagaliv/shared';

interface DashboardStats {
  totalUsers: number;
  ridesToday: number;
  commissionsToday: number;
  activeAgents: number;
}

interface RowRide {
  id: string;
  type: string;
  status: string;
  pickupAddress: string;
  dropoffAddress: string;
  priceFcfa: number;
  createdAt: string;
}

export default function Dashboard() {
  const stats = useFetch<DashboardStats>('/admin/dashboard');
  const rides = useFetch<PaginatedResponse<RowRide>>('/admin/rides');

  const cards = [
    { label: 'Total utilisateurs', value: stats.data ? stats.data.totalUsers.toLocaleString('fr-FR') : '…', icon: Users, to: '/users' },
    { label: "Courses aujourd'hui", value: stats.data ? stats.data.ridesToday.toLocaleString('fr-FR') : '…', icon: Truck, to: '/rides' },
    { label: 'Revenus du jour', value: stats.data ? formatFcfa(stats.data.commissionsToday) : '…', icon: DollarSign, to: '/revenue' },
    { label: 'Agents actifs', value: stats.data ? stats.data.activeAgents.toLocaleString('fr-FR') : '…', icon: CheckCircle, to: '/users' },
  ];

  const recentRides = (rides.data?.items ?? []).slice(0, 5);

  return (
    <div className="page-transition">
      <div className="container pt-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-text">Tableau de bord</h1>
        </div>

        {stats.error && (
          <div className="card" style={{ marginBottom: 16 }}>
            <span style={{ color: 'var(--color-error)', fontSize: 14 }}>{stats.error}</span>
          </div>
        )}

        <div className="stats-grid">
          {cards.map((stat, i) => (
            <Link key={i} to={stat.to} className="stat-card" style={{ textDecoration: 'none' }}>
              <div className="stat-card-header">
                <span className="stat-card-title">{stat.label}</span>
              </div>
              <div className="stat-card-value">{stat.value}</div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card">
            <h2 className="text-lg font-semibold text-text mb-4">Courses récentes</h2>
            {rides.isLoading ? (
              <div className="state-box"><span className="loading-spinner" /></div>
            ) : rides.error ? (
              <div className="state-box">{rides.error}</div>
            ) : !recentRides.length ? (
              <div className="state-box">Aucune course pour le moment.</div>
            ) : (
              <div className="space-y-3">
                {recentRides.map(ride => (
                  <Link key={ride.id} to="/rides" className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" style={{ textDecoration: 'none' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <Truck size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-text">{rideTypeLabels[ride.type] ?? ride.type}</p>
                        <p className="text-sm text-text-secondary">{ride.pickupAddress} → {ride.dropoffAddress}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-text">{formatFcfa(ride.priceFcfa)}</p>
                      <p className="text-xs text-text-secondary">{formatDate(ride.createdAt)}</p>
                      <span className={`badge ${rideStatusColors[ride.status] ?? 'badge-gray'}`}>{rideStatusLabels[ride.status] ?? ride.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <Link to="/rides" className="btn btn-outline btn-full mt-4">
              Voir toutes les courses <ArrowRight size={18} />
            </Link>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-text mb-4">Actions rapides</h2>
            <div className="space-y-3">
              <Link to="/users" className="btn btn-outline btn-full flex items-center justify-center gap-3">
                <Users size={20} /> Gérer les utilisateurs
              </Link>
              <Link to="/rides" className="btn btn-outline btn-full flex items-center justify-center gap-3">
                <Truck size={20} /> Voir toutes les courses
              </Link>
              <Link to="/revenue" className="btn btn-outline btn-full flex items-center justify-center gap-3">
                <DollarSign size={20} /> Rapports financiers
              </Link>
              <Link to="/settings" className="btn btn-outline btn-full flex items-center justify-center gap-3">
                <Settings size={20} /> Paramètres
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
